
import { supabase } from "@/integrations/supabase/client";
import { ShoppingList } from "@/types";
import {
  mapItemFromDatabase,
  mapListFromDatabase,
  SHOPPING_ITEM_COLUMNS,
  SHOPPING_LIST_COLUMNS,
} from "../utils";

// Function to get all shopping lists
export const getShoppingLists = async (): Promise<ShoppingList[]> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error('Error getting current user:', userError);
      return [];
    }

    if (!userData.user) {
      console.error('User must be authenticated');
      return [];
    }

    const { data: listsData, error } = await supabase
      .from('shopping_lists')
      .select(SHOPPING_LIST_COLUMNS)
      .eq('user_id', userData.user.id)
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error retrieving lists:', error);
      return [];
    }

    // For each list, get its items
    const lists: ShoppingList[] = [];
    
    for (const list of listsData || []) {
      try {
        const { data: itemsData, error: itemsError } = await supabase
          .from('shopping_items')
          .select(SHOPPING_ITEM_COLUMNS)
          .eq('list_id', list.id)
          .eq('user_id', userData.user.id)
          .order('created_at', { ascending: false });
        
        if (itemsError) {
          console.error(`Error retrieving items for list ${list.id}:`, itemsError);
          continue;
        }

        lists.push({
          ...mapListFromDatabase(list),
          items: itemsData ? itemsData.map(item => mapItemFromDatabase(item)) : []
        });
      } catch (err) {
        console.error(`Error processing list ${list.id}:`, err);
      }
    }

    return lists;
  } catch (err) {
    console.error('General error retrieving lists:', err);
    return [];
  }
};

// Function to get a single shopping list
export const getShoppingList = async (listId: string): Promise<ShoppingList | null> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error('Error getting current user:', userError);
      return null;
    }

    if (!userData.user) {
      console.error('User must be authenticated');
      return null;
    }

    const { data: list, error } = await supabase
      .from('shopping_lists')
      .select(SHOPPING_LIST_COLUMNS)
      .eq('id', listId)
      .eq('user_id', userData.user.id)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.error(`Error retrieving list ${listId}:`, error);
      return null;
    }
    if (!list) {
      console.warn(`⚠️ List ${listId} not found`);
      return null;
    }

    const { data: items, error: itemsError } = await supabase
      .from('shopping_items')
      .select(SHOPPING_ITEM_COLUMNS)
      .eq('list_id', listId)
      .eq('user_id', userData.user.id)
      .order('category', { ascending: true })
      .order('name', { ascending: true });
    
    if (itemsError) {
      console.error(`Error retrieving items for list ${listId}:`, itemsError);
      return null;
    }

    return {
      ...mapListFromDatabase(list),
      items: items ? items.map(item => mapItemFromDatabase(item)) : []
    };
  } catch (err) {
    console.error(`General error retrieving list ${listId}:`, err);
    return null;
  }
};
